import { getLocalizedName } from './localization'
import type { components } from '../api/schema'

export type AssetRelation = components['schemas']['AssetRelation']
export type AssetRelationsBlock = components['schemas']['AssetRelationsBlock']
export type AssetRelationRef = components['schemas']['AssetRelationRef']
export type AssetRelationType = components['schemas']['AssetRelationType']

export type RelationDirection = 'outgoing' | 'incoming'

/** One merged row — an outgoing or incoming edge of a given type, rendered
 *  from the focal asset's point of view. */
export interface RelationRow {
  uid: string
  direction: RelationDirection
  /** The *other* asset (target for outgoing, source for incoming). */
  counterpart: AssetRelationRef
  /** Localized role label naming the side the focal asset plays: source_role
   *  for outgoing rows, target_role for incoming rows. */
  roleLabel: string
}

/** One direction of one relation type — the level the panel actually renders
 *  as a labeled list ("Dessert" vs "Desservi par"). */
export interface RelationDirectionGroup {
  code: string
  direction: RelationDirection
  /** Localized role noun for the side the FOCAL asset plays. */
  focalRole: string
  /** Localized role noun for the side the COUNTERPART plays. */
  counterpartRole: string
  rows: RelationRow[]
}

/** One relation-type subsection: catalog metadata plus its merged rows. */
export interface RelationTypeGroup {
  code: string
  name: string
  emoji?: string | null
  color?: string | null
  /** Catalog `ordering` — drives group order, name is only the tie-breaker. */
  ordering?: number | null
  /** Catalog `aggregating` flag — drives the composition/functional split and
   *  the "rolls up" indicator. */
  aggregating: boolean
  /** Every row of the type, both directions (kept for compatibility). */
  rows: RelationRow[]
  /** The same rows split per direction, outgoing first. */
  directions: RelationDirectionGroup[]
}

export interface GroupedRelations {
  /** Relation types with relation_type_details.aggregating === true — "counts
   *  as part of" links that feed rollups (e.g. group_member, part_of). */
  composition: RelationTypeGroup[]
  /** Every other relation type — pure arrows (attached_to, monitors,
   *  serves_distribution, and any custom org-defined type). */
  functional: RelationTypeGroup[]
  /** Total number of edges, both directions, all types. */
  total: number
}

interface GroupAccumulator {
  details: AssetRelation['relation_type_details']
  rows: RelationRow[]
}

/** Catalog metadata indexed by relation type code. Optional enrichment: the
 *  embedded `relation_type_details` carries name/emoji/color/aggregating but
 *  NOT `ordering`, so the panel passes the org catalog in to order groups the
 *  way an admin configured them. */
export type RelationTypeCatalog = Record<string, AssetRelationType>

/** A relation-type predicate: in `include` when given, never in `exclude`.
 *  A row without a type never passes. */
export function relationTypeFilter(
  include?: string[],
  exclude?: string[],
): (code: string | undefined) => boolean {
  return (code) => {
    if (!code) return false
    if (include?.length && !include.includes(code)) return false
    if (exclude?.length && exclude.includes(code)) return false
    return true
  }
}

/** Keeps the relations of the allowed types, both directions: what a filtered
 *  panel (the Data tab's easements, spec 2026-10-25-24) hands to
 *  `groupAssetRelations`. */
export function filterRelationsBlock(
  relations: AssetRelationsBlock | null | undefined,
  allowed: (code: string | undefined) => boolean,
): AssetRelationsBlock | null | undefined {
  if (!relations) return relations
  return {
    ...relations,
    outgoing: (relations.outgoing ?? []).filter((r) => allowed(r.relation_type)),
    incoming: (relations.incoming ?? []).filter((r) => allowed(r.relation_type)),
  }
}

/**
 * Groups a focal asset's relations block into Composition / Functional
 * sections, one subsection per relation type and one list per direction,
 * driven entirely by the catalog's `aggregating` flag and role labels — never
 * by hardcoded relation-type codes, so a custom type an org adds later lands
 * in the right section with the right wording automatically.
 *
 * A row missing `relation_type_details` (defensive — shouldn't happen once
 * the backend always embeds it) is treated as functional, with the raw code
 * standing in for the name.
 *
 * Structural parent (`parent_asset_uid`) is not a relation row and never
 * appears here — the breadcrumb owns it.
 */
export function groupAssetRelations(
  relations: AssetRelationsBlock | null | undefined,
  locale: string,
  catalog?: RelationTypeCatalog,
): GroupedRelations {
  const byCode = new Map<string, GroupAccumulator>()
  let total = 0

  const addRow = (r: AssetRelation, direction: RelationDirection) => {
    const code = r.relation_type
    if (!code) return
    const details = r.relation_type_details
    let entry = byCode.get(code)
    if (!entry) {
      entry = { details, rows: [] }
      byCode.set(code, entry)
    } else if (!entry.details && details) {
      entry.details = details
    }
    const counterpart = direction === 'outgoing' ? r.target : r.source
    if (!counterpart) return
    entry.rows.push({
      uid: r.uid,
      direction,
      counterpart,
      roleLabel: focalRoleLabel(code, direction, details, catalog?.[code], locale),
    })
    total++
  }

  for (const r of relations?.outgoing ?? []) addRow(r, 'outgoing')
  for (const r of relations?.incoming ?? []) addRow(r, 'incoming')

  const composition: RelationTypeGroup[] = []
  const functional: RelationTypeGroup[] = []

  for (const [code, entry] of byCode) {
    const cat = catalog?.[code]
    const aggregating = Boolean(entry.details?.aggregating ?? cat?.aggregating)
    const group: RelationTypeGroup = {
      code,
      name:
        getLocalizedName(entry.details?.name, locale)
        || getLocalizedName(cat?.name, locale)
        || code,
      emoji: entry.details?.emoji ?? cat?.emoji,
      color: entry.details?.color ?? cat?.color,
      ordering: cat?.ordering ?? null,
      aggregating,
      rows: entry.rows,
      directions: buildDirectionGroups(code, entry.rows, entry.details, cat, locale),
    }
    if (aggregating) composition.push(group)
    else functional.push(group)
  }

  composition.sort(compareGroups)
  functional.sort(compareGroups)

  return { composition, functional, total }
}

/** Catalog `ordering` first (nulls last), localized name as tie-breaker. */
function compareGroups(a: RelationTypeGroup, b: RelationTypeGroup): number {
  const ao = a.ordering ?? Number.MAX_SAFE_INTEGER
  const bo = b.ordering ?? Number.MAX_SAFE_INTEGER
  if (ao !== bo) return ao - bo
  return a.name.localeCompare(b.name)
}

function buildDirectionGroups(
  code: string,
  rows: RelationRow[],
  details: AssetRelation['relation_type_details'],
  cat: AssetRelationType | undefined,
  locale: string,
): RelationDirectionGroup[] {
  const out: RelationDirectionGroup[] = []
  for (const direction of ['outgoing', 'incoming'] as const) {
    const dirRows = rows.filter((r) => r.direction === direction)
    if (dirRows.length === 0) continue
    out.push({
      code,
      direction,
      focalRole: focalRoleLabel(code, direction, details, cat, locale),
      counterpartRole: focalRoleLabel(
        code,
        direction === 'outgoing' ? 'incoming' : 'outgoing',
        details,
        cat,
        locale,
      ),
      rows: dirRows,
    })
  }
  return out
}

/**
 * The localized role noun naming the side the focal asset plays for an edge of
 * `code` in `direction`. Pure catalog lookup — `source_role` when the focal
 * asset is the source, `target_role` when it is the target — so no relation
 * type is ever phrased in the frontend.
 */
export function focalRoleLabel(
  code: string,
  direction: RelationDirection,
  details: AssetRelation['relation_type_details'] | undefined,
  cat: AssetRelationType | undefined,
  locale: string,
): string {
  const map = direction === 'outgoing'
    ? (details?.source_role ?? cat?.source_role)
    : (details?.target_role ?? cat?.target_role)
  return getLocalizedName(map ?? undefined, locale) || code
}

/** Localized name of a catalog relation type, falling back to its code. */
export function relationTypeName(
  rt: { code: string, name?: Record<string, string> | null } | undefined,
  locale: string,
): string {
  if (!rt) return ''
  return getLocalizedName(rt.name ?? undefined, locale) || rt.code
}
